import { QueryKey } from '@tanstack/react-query'
import { useClient, useMutation } from '@weenat/client'
import { extractAvailableHorizonIdsSet, HorizonOpt } from '@weenat/client/dist/core/horizons'
import { metricsAndLabelKeyDisplayedByMetric } from '@weenat/client/dist/core/my-farm'
import { computeHasNoDataForHorizon } from '@weenat/client/dist/core/summary'
import {
  BackgroundMapUniverseMetric,
  getAvailableUniversesFromMetrics,
  getMetricsFromSummary,
  Universe
} from '@weenat/client/dist/core/universes'
import { PlotOrStationSummary } from '@weenat/client/dist/resources/measurements'
import { isPlotSummary, isStationSummary } from '@weenat/client/dist/resources/measurements.type'
import { useIntl } from '@weenat/wintl'
import { useOrgContext } from 'app/orgProvider'
import { useNavigate } from 'app/routx-router'
import useUniverseBackgroundMapContext from 'app/src/dashboard/components/DashboardMap/universes/useUniverseBackgroundMapContext'
import useDisclosure from 'app/src/hooks/useDisclosure'
import Card from 'app/src/kit/Card'
import DelimitedFlex from 'app/src/kit/DelimitedFlex'
import Link from 'app/src/kit/LinkComponent'
import ListEmpty from 'app/src/kit/ListEmpty'
import Text from 'app/src/kit/Text'
import ViewKindSelector from 'app/src/kit/ViewKindSelector'
import { useUniverse } from 'app/state'
import isNil from 'lodash-es/isNil'
import { Fragment, memo, useCallback, useMemo, useRef } from 'react'
import { Code as ContentLoader } from 'react-content-loader'
import { createPortal } from 'react-dom'
import { List as FixedSizeList, RowComponentProps } from 'react-window'
import { styled } from 'styled-components'
import { UniverseCardSelectorModal } from '../../../myFarm/tools/UniverseSelector'
import { useBackgroundMapDispatcher } from './contexts/BackgroundMapContext'
import FetchSummariesError from './FetchSummariesError'
import NoSummaries from './NoSummaries'
import NoSummariesMatchingSearchTermContent from './NoSummariesMatchingSearchTermContent'
import PlotOrStationCard, {
  PLOT_OR_STATION_CARD_BORDER_WIDTH,
  PLOT_OR_STATION_CARD_HEIGHT
} from './PlotOrStationCard'

// Adding plus one for the borders to display
const ROW_HEIGHT = PLOT_OR_STATION_CARD_HEIGHT + PLOT_OR_STATION_CARD_BORDER_WIDTH

const emptyArray: HorizonOpt[] = []

const HEADER_RIGHT_MARGIN = 96

export const METRIC_COLUMN_WIDTH = 96
export const METRIC_COLUMN_GAP = 8

const LIST_CONTAINER_ID = 'list-container'
const EMPTY_LIST_CONTAINER_ID = 'empty-list-container'

const ListHeaderContainer = styled(Flex)`
  border-color: ${(p) => p.theme.colors.grayscale['100']};
  border-width: 0 0 1px 0;
  border-style: solid;
  padding-right: ${HEADER_RIGHT_MARGIN}px;
`

const RowCard = styled(Card)`
  overflow: hidden;
  height: 100%;
  transition: border-color 0.2s ease-in-out;
`

interface FilteredElementInfoProps {
  hiddenElementsCount: number | undefined
  portalTargetId?: string
  pb?: number
}

const FilteredElementInfo: FC<FilteredElementInfoProps> = ({
  hiddenElementsCount,
  portalTargetId,
  pb
}) => {
  const { t } = useIntl()
  const client = useClient()

  const [updatePreferences] = useMutation(client.preferences.update())

  const dispatch = useBackgroundMapDispatcher()

  const target = document.getElementById(portalTargetId)

  if (isNil(target)) return null

  if (isNil(hiddenElementsCount) || hiddenElementsCount === 0) return null

  return createPortal(
    <Flex $justifyContent='center' $alignItems='center' $p='lg' $pb={pb}>
      <Text>
        {hiddenElementsCount === 1
          ? t('models.plot.misc.singular_item_count_hidden', { count: hiddenElementsCount })
          : t('models.plot.misc.items_count_hidden', { count: hiddenElementsCount })}
        {'. '}
        <Link
          $underlined
          onPress={() => {
            updatePreferences({
              display: {
                hidePlots: false,
                hideStations: false,
                hideVirtualWeatherStations: false,
                onlyFavorites: false,
                hideEmptyDataPOD: false
              }
            })
            dispatch({ type: 'setSearch', newSearch: '' })
          }}
        >
          {t('models.plot.actions.show_hidden_elements')}
        </Link>
      </Text>
    </Flex>,
    target
  )
}

interface ListItemData {
  currentItems: PlotOrStationSummary[]
  focusedHorizon: number | null
  focusedMetric: BackgroundMapUniverseMetric | undefined
  hasAlreadyFetchedAndHasNoItems: boolean
  hasNoItemsMatchingSearchTerm: boolean
  isError: boolean
  isLoading: boolean
  summariesQueryCacheKey: QueryKey
}

interface RowProps extends RowComponentProps, ListItemData {
  availableHorizonOptions: HorizonOpt[]
  onHorizonChange: (newHorizon: number | null) => void
}

const Row: FC<RowProps> = memo(
  ({
    index,
    style,
    currentItems,
    hasAlreadyFetchedAndHasNoItems,
    hasNoItemsMatchingSearchTerm,
    isError,
    isLoading,
    focusedMetric,
    focusedHorizon,
    availableHorizonOptions,
    onHorizonChange,
    summariesQueryCacheKey
  }) => {
    const [universe] = useUniverse()
    const org = useOrgContext()
    const dispatch = useBackgroundMapDispatcher()
    const { isOpen, toggle, close } = useDisclosure()
    const nav = useNavigate()
    const [, setUniverse] = useUniverse()

    const summary = currentItems[index]

    const isNotRenderingSummary =
      isLoading || isError || hasAlreadyFetchedAndHasNoItems || hasNoItemsMatchingSearchTerm

    const hasNoDataForHorizon = useMemo(
      () =>
        computeHasNoDataForHorizon({
          metric: focusedMetric,
          horizonId: focusedHorizon,
          summary
        }),
      [focusedMetric, focusedHorizon, summary]
    )

    const availableUniverses = useMemo(() => {
      const measures = getMetricsFromSummary(summary)
      return getAvailableUniversesFromMetrics(measures)
    }, [summary])

    const availableHorizons = useMemo(() => {
      if (availableHorizonOptions === undefined) return []
      if (focusedMetric === 'HPOT' || focusedMetric === 'T_SOIL_BY_HORIZON') {
        const horizonIdsOnPlot = Array.from(extractAvailableHorizonIdsSet(summary))
        return availableHorizonOptions.filter((hOpt) => horizonIdsOnPlot.includes(hOpt.value))
      } else {
        return emptyArray
      }
    }, [focusedMetric, summary, availableHorizonOptions])

    const itemContent = (
      <PlotOrStationCard
        summary={summary}
        summariesQueryCacheKey={summariesQueryCacheKey}
        onHorizonChange={onHorizonChange}
        availableHorizonOptions={availableHorizons}
        hasNoDataForHorizon={hasNoDataForHorizon}
        focusedHorizon={focusedHorizon}
        focusedMetric={focusedMetric}
      />
    )

    const content = isLoading ? (
      // Height is important otherwise not displayed
      <ContentLoader height='100%' uniqueKey='fetchSummariesLoader' width='100%' />
    ) : isError ? (
      <FetchSummariesError />
    ) : hasAlreadyFetchedAndHasNoItems ? (
      <NoSummaries />
    ) : hasNoItemsMatchingSearchTerm ? (
      <NoSummariesMatchingSearchTermContent />
    ) : (
      itemContent
    )

    const MaybeLink = isNotRenderingSummary ? Fragment : Link

    const currentItemId = isPlotSummary(summary)
      ? summary.metadata.plot.id
      : summary.metadata.station.id

    const maybeLinkProps = isNotRenderingSummary
      ? {}
      : {
          to: isPlotSummary(summary)
            ? `/farms/${org.id}/plots/${summary.metadata.plot.id}/${
                universe === Universe.irrelis ? ('irrelis' as const) : ''
              }`
            : `/farms/${org.id}/stations/${summary.metadata.station.id}`
        }

    const outerBoxStyle = useMemo(
      () => ({
        ...style,
        height:
          hasNoItemsMatchingSearchTerm || hasAlreadyFetchedAndHasNoItems ? 'unset' : style?.height
      }),
      [style, hasNoItemsMatchingSearchTerm, hasAlreadyFetchedAndHasNoItems]
    )

    const handleMouseEnter = useCallback(() => {
      if (!isNotRenderingSummary) {
        dispatch({
          type: 'setHoverIntent',
          newHoverIntent: {
            itemId: currentItemId,
            source: 'list'
          }
        })
      }
    }, [isNotRenderingSummary, dispatch, currentItemId])

    const handleClickOnOverviewElement = useCallback(
      () => {
        if (availableUniverses.length > 1) {
          return toggle()
        } else if (availableUniverses.length === 1 && !isNil(maybeLinkProps.to)) {
          setUniverse(availableUniverses[0])
          return nav(maybeLinkProps.to)
        }
      },
      // Never add nav to dependency arrays
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [availableUniverses, maybeLinkProps.to, setUniverse, toggle]
    )

    return (
      // The outer box is needed otherwise card margin won't properly apply, and we need to pass style given by the list
      <Box style={outerBoxStyle}>
        {(universe !== Universe.overview || isStationSummary(summary)) && (
          <MaybeLink {...maybeLinkProps}>
            {isNotRenderingSummary ? (
              <RowCard onMouseEnter={handleMouseEnter}>{content}</RowCard>
            ) : (
              content
            )}
          </MaybeLink>
        )}
        {universe === Universe.overview && isPlotSummary(summary) && (
          <>
            <Flex onClick={handleClickOnOverviewElement}>
              {isNotRenderingSummary ? (
                <RowCard onMouseEnter={handleMouseEnter}>{content}</RowCard>
              ) : (
                content
              )}
            </Flex>
            {availableUniverses.length > 1 && (
              <UniverseCardSelectorModal
                isOpen={isOpen}
                summary={summary}
                summaryLink={maybeLinkProps.to!}
                handleClose={close}
              />
            )}
          </>
        )}
      </Box>
    )
  }
)

interface PlotListProps {
  currentItems: PlotOrStationSummary[]
  focusedHorizon: number | null
  hasAlreadyFetchedAndHasNoItems: boolean
  hiddenElementsCount: number
  isError: boolean
  isExpertOrg: boolean
  isLoading: boolean
  summariesQueryCacheKey: QueryKey
}

const PlotOrStationAutosizeList: FC<PlotListProps> = ({
  currentItems,
  focusedHorizon,
  hasAlreadyFetchedAndHasNoItems,
  hiddenElementsCount,
  isError,
  isLoading,
  summariesQueryCacheKey
}) => {
  const { focusedMetricId } = useUniverseBackgroundMapContext()
  const listRef = useRef<typeof FixedSizeList<ListItemData>>(null)

  const itemData: ListItemData = {
    isLoading,
    isError,
    currentItems,
    hasAlreadyFetchedAndHasNoItems,
    hasNoItemsMatchingSearchTerm: currentItems.length === 0,
    summariesQueryCacheKey,
    focusedHorizon,
    focusedMetric: focusedMetricId
  }

  return (
    <>
      {currentItems.length === 0 ? (
        <DelimitedFlex
          id={EMPTY_LIST_CONTAINER_ID}
          $flexDirection='column'
          $justifyContent='center'
          $backgroundColor={'grayscale.white'}
        >
          <ListEmpty isFetchPending={isLoading} />
        </DelimitedFlex>
      ) : (
        <FixedSizeList
          id={LIST_CONTAINER_ID}
          ref={listRef}
          rowHeight={ROW_HEIGHT}
          rowComponent={Row}
          rowCount={currentItems.length}
          rowProps={{ ...itemData }}
          style={{ paddingBottom: 80 }}
        />
      )}

      <FilteredElementInfo
        pb={currentItems.length === 0 ? 16 : 64}
        portalTargetId={currentItems.length === 0 ? EMPTY_LIST_CONTAINER_ID : LIST_CONTAINER_ID}
        hiddenElementsCount={hiddenElementsCount}
      />
    </>
  )
}

const PlotOrStationListHeader: FC<{
  focusedMetric: BackgroundMapUniverseMetric | undefined
}> = ({ focusedMetric }) => {
  const { t } = useIntl()

  return (
    <ListHeaderContainer $flexDirection='row' $justifyContent='flex-end' $py='lg' $px='md' $mr={0}>
      {Object.values(metricsAndLabelKeyDisplayedByMetric(focusedMetric))?.map((labelKey) => (
        <Flex
          $width={METRIC_COLUMN_WIDTH + METRIC_COLUMN_GAP}
          key={labelKey}
          $justifyContent='flex-end'
        >
          <Text $lineHeight='none' $fontWeight='bold' $color={'grayscale.black'} $textAlign='end'>
            {t(`${labelKey}`, { capitalize: true })}
          </Text>
        </Flex>
      ))}
    </ListHeaderContainer>
  )
}

const AbsolutelyPositionedSwitch = styled(ViewKindSelector)`
  position: absolute;
  bottom: 16px;
`

interface PlotOrStationListProps {
  currentItems: PlotOrStationSummary[]
  focusedHorizon: number | null
  hasAlreadyFetchedAndHasNoItems: boolean
  hiddenElementsCount: number
  isError: boolean
  isExpertOrg: boolean
  isLoading: boolean
  summariesQueryCacheKey: QueryKey
}

const PlotOrStationList = ({
  currentItems,
  focusedHorizon,
  hasAlreadyFetchedAndHasNoItems,
  hiddenElementsCount,
  isError,
  isExpertOrg,
  isLoading,
  summariesQueryCacheKey
}: PlotOrStationListProps) => {
  const { focusedMetricId } = useUniverseBackgroundMapContext()
  const [universe] = useUniverse()

  return (
    <Flex $flex={1} $flexDirection='column' $backgroundColor={'grayscale.50'} $pointerEvents='auto'>
      {universe !== Universe.overview && (
        <PlotOrStationListHeader focusedMetric={focusedMetricId} />
      )}
      <Flex $flex={1} $flexDirection='column'>
        <PlotOrStationAutosizeList
          currentItems={currentItems}
          focusedHorizon={focusedHorizon}
          hasAlreadyFetchedAndHasNoItems={hasAlreadyFetchedAndHasNoItems}
          hiddenElementsCount={hiddenElementsCount}
          isError={isError}
          isExpertOrg={isExpertOrg}
          isLoading={isLoading}
          summariesQueryCacheKey={summariesQueryCacheKey}
        />
        <AbsolutelyPositionedSwitch />
      </Flex>
    </Flex>
  )
}

export default PlotOrStationList
